import { useEffect, useRef, useState } from 'react';
import { ArrowUp, Calendar, CircleDot, Flag, FolderClosed, Pencil, Trash2, Users, X } from 'lucide-react';
import { PRIORITY_LABELS, type Comment, type Task } from '@unitly/contracts';
import { Sheet } from '../../ui/Sheet.tsx';
import { Avatar, PageLoader, wsColor } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import {
  useAddComment, useComments, useDeleteComment, useDeleteTask, useEditComment, useMembers, useStates, useTask, useToggleTask,
  useUpdateTask, useWorkspaces,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { relativeTime } from '../../lib/dates.ts';
import { TaskCheck } from './TaskRow.tsx';
import { ApiError } from '../../api/client.ts';

function useAutosize(ref: React.RefObject<HTMLTextAreaElement>, value: string) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${el.scrollHeight}px`;
  }, [ref, value]);
}

export function TaskDetailSheet({ taskId, onClose }: { taskId: string | null; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const task = useTask(orgId, taskId);
  return (
    <Sheet open={Boolean(taskId)} onClose={onClose} full labelledBy="task-detail-title" headerActions={<span />}>
      {task.isLoading && <PageLoader />}
      {task.error && (
        <div className="empty">
          <strong>{task.error instanceof ApiError && task.error.status === 404 ? 'Task non trovata' : 'Impossibile aprire la task'}</strong>
          <p>{task.error instanceof ApiError && task.error.status === 404 ? 'Potrebbe essere stata eliminata o non hai accesso.' : task.error.message}</p>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Chiudi</button>
        </div>
      )}
      {task.data && <TaskDetail key={task.data.id} task={task.data} onClose={onClose} />}
    </Sheet>
  );
}

function TaskDetail({ task, onClose }: { task: Task; onClose: () => void }) {
  const { orgId, detail } = useOrgCtx();
  const update = useUpdateTask(orgId);
  const toggle = useToggleTask(orgId);
  const remove = useDeleteTask(orgId);
  const states = useStates(orgId);
  const workspaces = useWorkspaces(orgId);
  const members = useMembers(orgId, detail?.role !== 'guest');
  const toast = useToast();
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [showAllPeople, setShowAllPeople] = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  useAutosize(titleRef, title);
  useAutosize(descRef, description);
  const canEdit = task.permissions.edit;

  const save = (patch: Parameters<typeof update.mutate>[0]['patch']) =>
    update.mutate(
      { id: task.id, patch },
      { onError: (e) => toast(e instanceof ApiError ? e.message : 'Modifica non salvata') },
    );

  const assigneeIds = task.assignees.map((a) => a.id);
  const people = members.data?.map((m) => ({ id: m.userId, displayName: m.displayName, avatarUrl: m.avatarUrl })) ?? task.assignees;
  const sortedPeople = [...people].sort((a, b) => Number(assigneeIds.includes(b.id)) - Number(assigneeIds.includes(a.id)));
  const visiblePeople = showAllPeople ? sortedPeople : sortedPeople.slice(0, Math.max(6, assigneeIds.length));

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <TaskCheck task={task} onToggle={(done) => toggle.mutate({ id: task.id, done })} />
        <span className="detail-key">{task.key}</span>
        <span style={{ flex: 1 }} />
        {task.permissions.delete && (
          <button
            type="button"
            className="icon-btn muted"
            aria-label="Elimina task"
            onClick={() => {
              if (!confirm(`Eliminare "${task.title}"?`)) return;
              remove.mutate(task.id, { onSuccess: () => { toast('Task eliminata'); onClose(); } });
            }}
          >
            <Trash2 size={18} />
          </button>
        )}
        <button type="button" className="btn btn-plain btn-sm" onClick={onClose} style={{ fontWeight: 600 }}>
          Fine
        </button>
      </div>

      <textarea
        id="task-detail-title"
        ref={titleRef}
        className="detail-title"
        value={title}
        rows={1}
        readOnly={!canEdit}
        aria-label="Titolo"
        onChange={(e) => setTitle(e.target.value.replace(/\n/g, ' '))}
        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), (e.target as HTMLTextAreaElement).blur())}
        onBlur={() => {
          const t = title.trim();
          if (t && t !== task.title) save({ title: t });
          else setTitle(task.title);
        }}
      />

      <div className="card list props">
        <label className="prop">
          <span><CircleDot size={17} /> Stato</span>
          <select value={task.state.id} disabled={!canEdit} onChange={(e) => save({ stateId: e.target.value })}>
            {states.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <div className="prop">
          <span><Calendar size={17} /> Scadenza</span>
          <span className="inline">
            <input
              type="date"
              aria-label="Data di scadenza"
              value={task.dueDate ?? ''}
              disabled={!canEdit}
              style={{ width: 'auto', flex: 1, minWidth: 130 }}
              onChange={(e) => save({ dueDate: e.target.value || null, ...(e.target.value ? {} : { dueTime: null }) })}
            />
            {task.dueDate && (
              <input
                type="time"
                aria-label="Ora"
                value={task.dueTime ?? ''}
                disabled={!canEdit}
                style={{ width: 'auto' }}
                onChange={(e) => save({ dueTime: e.target.value || null })}
              />
            )}
          </span>
        </div>
        <label className="prop">
          <span><Flag size={17} /> Priorità</span>
          <select value={task.priority} disabled={!canEdit} onChange={(e) => save({ priority: Number(e.target.value) })}>
            {[1, 2, 3, 4, 0].map((p) => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
          </select>
        </label>
        <label className="prop">
          <span><FolderClosed size={17} /> Spazio</span>
          <span className="inline">
            <span className="ws-dot" style={{ '--ws-color': wsColor(task.workspaceColor) } as React.CSSProperties} />
            <select value={task.workspaceId} disabled={!canEdit} onChange={(e) => save({ workspaceId: e.target.value })} style={{ flex: 1 }}>
              {(workspaces.data ?? [{ id: task.workspaceId, name: task.workspaceName }]).map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </span>
        </label>
        <div className="prop" style={{ alignItems: 'start', paddingTop: 12, paddingBottom: 12 }}>
          <span style={{ paddingTop: 4 }}><Users size={17} /> Persone</span>
          <div className="people-picker">
            {visiblePeople.map((p) => {
              const on = assigneeIds.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className="person-chip"
                  aria-pressed={on}
                  disabled={!canEdit}
                  onClick={() => save({ assigneeIds: on ? assigneeIds.filter((id) => id !== p.id) : [...assigneeIds, p.id] })}
                >
                  <Avatar id={p.id} name={p.displayName} avatarUrl={p.avatarUrl} size={24} />
                  {p.displayName.split(' ')[0]}
                </button>
              );
            })}
            {sortedPeople.length > visiblePeople.length && (
              <button type="button" className="btn btn-plain btn-sm" onClick={() => setShowAllPeople(true)}>
                Altre {sortedPeople.length - visiblePeople.length}
              </button>
            )}
            {!people.length && <span className="hint">Nessun assegnatario</span>}
          </div>
        </div>
      </div>

      <textarea
        ref={descRef}
        className="textarea description"
        placeholder={canEdit ? 'Aggiungi note, link, dettagli…' : 'Nessuna nota'}
        value={description}
        readOnly={!canEdit}
        aria-label="Note"
        onChange={(e) => setDescription(e.target.value)}
        onBlur={() => description !== task.description && save({ description })}
      />

      <p className="hint" style={{ marginTop: 10 }}>
        Creata da {task.createdBy.displayName} · {relativeTime(task.createdAt)}
      </p>

      <Comments task={task} />
    </>
  );
}

function CommentRow({ comment, orgId, taskId }: { comment: Comment; orgId: string; taskId: string }) {
  const edit = useEditComment(orgId, taskId);
  const del = useDeleteComment(orgId, taskId);
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const ref = useRef<HTMLTextAreaElement>(null);
  useAutosize(ref, draft);

  if (editing) {
    return (
      <div className="comment" key={comment.id}>
        <Avatar id={comment.author.id} name={comment.author.displayName} size={30} />
        <div className="comment-bubble" style={{ flex: 1 }}>
          <div className="composer" style={{ padding: 0 }}>
            <textarea ref={ref} className="textarea" rows={1} autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button type="button" className="btn btn-plain btn-sm" onClick={() => setEditing(false)}><X size={14} /> Annulla</button>
            <button
              type="button" className="btn btn-primary btn-sm" disabled={!draft.trim() || edit.isPending}
              onClick={() => edit.mutate({ id: comment.id, body: draft.trim() }, { onSuccess: () => setEditing(false), onError: (e) => toast(e instanceof ApiError ? e.message : 'Modifica non riuscita') })}
            >
              Salva
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="comment" key={comment.id}>
      <Avatar id={comment.author.id} name={comment.author.displayName} size={30} />
      <div className="comment-bubble">
        <div className="comment-head">
          <strong>{comment.author.displayName}</strong>
          <time dateTime={comment.createdAt}>{relativeTime(comment.createdAt)}</time>
          {comment.editedAt && <span className="secondary">· modificato</span>}
          {comment.mine && (
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
              <button type="button" className="icon-btn muted" style={{ width: 26, height: 26 }} aria-label="Modifica commento" onClick={() => { setDraft(comment.body); setEditing(true); }}><Pencil size={13} /></button>
              <button
                type="button" className="icon-btn muted" style={{ width: 26, height: 26 }} aria-label="Elimina commento"
                onClick={() => { if (confirm('Eliminare questo commento?')) del.mutate(comment.id); }}
              >
                <Trash2 size={13} />
              </button>
            </span>
          )}
        </div>
        <div className="comment-body">{comment.body}</div>
      </div>
    </div>
  );
}

function Comments({ task }: { task: Task }) {
  const { orgId } = useOrgCtx();
  const comments = useComments(orgId, task.id);
  const add = useAddComment(orgId, task.id);
  const [body, setBody] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);
  useAutosize(ref, body);
  const send = () => {
    const text = body.trim();
    if (!text) return;
    add.mutate(text, { onSuccess: () => setBody('') });
  };
  return (
    <>
      <h3 className="subhead">Commenti</h3>
      <div className="comments">
        {comments.data?.length === 0 && <p className="hint">Nessun commento. Scrivi qui invece che su WhatsApp: resta tutto nello storico della task.</p>}
        {comments.data?.map((c) => <CommentRow key={c.id} comment={c} orgId={orgId} taskId={task.id} />)}
      </div>
      {task.permissions.comment && (
        <div className="composer" style={{ marginTop: 16 }}>
          <textarea
            ref={ref}
            className="textarea"
            rows={1}
            placeholder="Scrivi un commento"
            value={body}
            aria-label="Nuovo commento"
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send();
            }}
          />
          <button type="button" className="send-btn" aria-label="Invia commento" disabled={!body.trim() || add.isPending} onClick={send}>
            <ArrowUp size={20} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </>
  );
}
